import { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';

import Page from '../../../components/atoms/page';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import TableWidget, { TableColumn } from '../../../components/molecules/tableWidget';

import {
  addWorkshopArea,
  fetchWorkshopAreas,
  removeWorkshopArea,
  WorkshopAreasResponse,
} from '../../../api/workshops';
import { useSelectedWorkshop } from '../../../context/workshopContext';

import theme from '../../../theme';

type DestinationRow = {
  id: string;
  name: string;
  workshopId: string;
  workshopName: string;
};

const columns: TableColumn[] = [{ id: 'name', label: 'Name' }];

const mapAreasToRows = (
  data: WorkshopAreasResponse | undefined,
  workshopId: string,
  workshopName: string
): DestinationRow[] =>
  (data?.items ?? []).map((area) => ({
    id: `${workshopId}-${area.name}`,
    name: area.name,
    workshopId,
    workshopName,
  }));

const Destinations = () => {
  const { selectedOrganisationId: workshopId, selectedWorkshop, isLoading: isLoadingWorkshops } =
    useSelectedWorkshop();
  const [openAddDestinationModal, setOpenAddDestinationModal] = useState(false);
  const [destinations, setDestinations] = useState<DestinationRow[]>([]);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(true);

  const selectedWorkshopName = selectedWorkshop?.organisationName ?? '';

  useEffect(() => {
    if (!workshopId) {
      if (!isLoadingWorkshops) {
        setIsLoadingDestinations(false);
        setDestinations([]);
      }
      return;
    }

    const loadAreas = async () => {
      setIsLoadingDestinations(true);
      try {
        const data = await fetchWorkshopAreas(workshopId);
        setDestinations(mapAreasToRows(data, workshopId, selectedWorkshopName));
      } catch (error) {
        console.log('error with fetching areas', error);
      } finally {
        setIsLoadingDestinations(false);
      }
    };

    loadAreas();
  }, [workshopId, selectedWorkshopName, isLoadingWorkshops]);

  useEffect(() => {
    if (!openAddDestinationModal) {
      return;
    }

    setName('');
  }, [openAddDestinationModal]);

  const closeModal = () => {
    setOpenAddDestinationModal(false);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();

    if (!trimmedName || !workshopId || !selectedWorkshop || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      await addWorkshopArea(workshopId, trimmedName);

      setDestinations((current) => [
        ...current,
        {
          id: `${workshopId}-${trimmedName}`,
          name: trimmedName,
          workshopId,
          workshopName: selectedWorkshop.organisationName,
        },
      ]);

      closeModal();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    const destination = destinations.find(
      (destination) => destination.id === id
    );

    if (!destination) {
      return;
    }
    try {
      await removeWorkshopArea(destination.workshopId, destination.name);

      setDestinations((current) =>
        current.filter((destination) => destination.id !== id)
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Page
        title="Destinations"
        linkTitle="add destination"
        onClickLink={() => setOpenAddDestinationModal(true)}
      >
        <TableWidget
          data={destinations}
          columns={columns}
          defaultSort="name"
          themes={{ device: 'desktop' }}
          pagination
          loadingStatus={isLoadingDestinations ? 'loading' : undefined}
          onRemove={(_event, id) => handleRemove(id)}
        />
      </Page>

      <Modal
        open={openAddDestinationModal}
        onClose={closeModal}
        title="Add destination"
        footer={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={closeModal}
              extend={{
                color: theme.colors.grey,
                fontSize: 16,
                padding: '8px',
              }}
            >
              CANCEL
            </Button>

            <Button
              onClick={handleSave}
              disabled={!name.trim() || !workshopId || isSaving}
              extend={{
                color: theme.colors.primary,
                fontSize: 16,
                padding: '8px',
              }}
            >
              SAVE
            </Button>
          </Box>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${2 * theme.layout.grid}px`,
          }}
        >
          <TextField
            label="Destination name"
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      </Modal>
    </>
  );
};

export default Destinations;
